import React, { useRef, useState } from 'react';
import { useChatStore } from '../../store/chatStore';
import type { AskQuestionItem } from '../../store/chatStore';
import { ChevronRightIcon, ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PenLine } from 'lucide-react';
import { ExtendedMarkdown } from './ExtendedMarkdown';

import { useTranslation } from 'react-i18next';

const PANEL_SHADOW = '0 -2px 16px 0 rgba(0,0,0,0.10), 0 -1px 4px 0 rgba(0,0,0,0.06)';
const BRAND_COLOR = 'var(--color-action-primary, #FF732E)';
const BANNER_STYLE = {
  backgroundColor: 'rgba(255, 115, 46, 0.07)',
  borderColor: 'rgba(255, 115, 46, 0.2)',
};

export const AskQuestionPanel: React.FC<{ isPreparing?: boolean }> = ({
  isPreparing = false,
}) => {
  const { t } = useTranslation('pleri/chat');
  const activeAskQuestion = useChatStore((s) => s.activeAskQuestion);
  const setActiveAskQuestion = useChatStore((s) => s.setActiveAskQuestion);
  const submitInputPrompt = useChatStore((s) => s.submitInputPrompt);
  const setInputPrompt = useChatStore((s) => s.setInputPrompt);

  const questions: AskQuestionItem[] = activeAskQuestion?.questions ?? [];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string | undefined>>({});
  const [allSelectedIndices, setAllSelectedIndices] = useState<
    Record<number, Set<number>>
  >({});
  const [otherValue, setOtherValue] = useState('');
  const [otherFocused, setOtherFocused] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const focusedIndexRef = useRef<number | null>(null);
  const completedRef = useRef(false);
  const clearFocusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (questions.length === 0) return null;

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex];
  const options = Array.isArray(currentQuestion?.options)
    ? currentQuestion.options
    : [];
  const isMultiSelect = Boolean(currentQuestion?.multiSelect);
  const hasPreview = options.some((o) => o.preview);
  const focusedOption = focusedIndex !== null ? options[focusedIndex] : null;
  const isLastQuestion = currentIndex === totalQuestions - 1;
  const selectedIndices = allSelectedIndices[currentIndex] ?? new Set<number>();

  // --- Preview hover (mousemove-based to avoid re-render loop) ---
  const cancelClearFocus = () => {
    if (clearFocusTimer.current) {
      clearTimeout(clearFocusTimer.current);
      clearFocusTimer.current = null;
    }
  };
  const scheduleClearFocus = () => {
    clearFocusTimer.current = setTimeout(() => {
      focusedIndexRef.current = null;
      setFocusedIndex(null);
    }, 150);
  };
  const handleOptionsMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = (e.target as HTMLElement).closest('[data-option-idx]');
    const raw = el?.getAttribute('data-option-idx');
    const parsed = raw != null ? parseInt(raw, 10) : null;
    const idx = parsed !== null && !isNaN(parsed) ? parsed : null;
    cancelClearFocus();
    if (idx !== focusedIndexRef.current) {
      focusedIndexRef.current = idx;
      setFocusedIndex(idx);
    }
  };

  // --- Answer flow ---
  const sendAllAnswers = (finalAnswers: Record<number, string | undefined>) => {
    if (completedRef.current) return;
    completedRef.current = true;
    const parts = questions
      .map((q, i) => (finalAnswers[i] ? `${q.question}: ${finalAnswers[i]}` : null))
      .filter(Boolean) as string[];
    const message =
      parts.length > 0
        ? parts.join('\n')
        : 'I skipped all questions — please proceed with your best judgment.';
    setInputPrompt(message);
    submitInputPrompt();
    setActiveAskQuestion(null);
  };

  const advanceOrReview = (updatedAnswers: Record<number, string | undefined>) => {
    setAnswers(updatedAnswers);
    if (isLastQuestion) {
      // Skip review screen when there's only one question — nothing to review
      if (totalQuestions === 1) {
        sendAllAnswers(updatedAnswers);
        return;
      }
      setShowReview(true);
    } else {
      setCurrentIndex((i) => i + 1);
      setOtherValue('');
      setFocusedIndex(null);
      focusedIndexRef.current = null;
    }
  };

  const handleOptionClick = (optionIndex: number) => {
    if (isMultiSelect) {
      const current = allSelectedIndices[currentIndex] ?? new Set<number>();
      const next = new Set(current);
      next.has(optionIndex) ? next.delete(optionIndex) : next.add(optionIndex);
      setAllSelectedIndices((prev) => ({ ...prev, [currentIndex]: next }));
    } else {
      advanceOrReview({ ...answers, [currentIndex]: options[optionIndex].label });
    }
  };

  const handleMultiSelectNext = () => {
    const indices = allSelectedIndices[currentIndex] ?? new Set<number>();
    const labels = [...indices]
      .sort((a, b) => a - b)
      .map((i) => options[i].label)
      .join(', ');
    advanceOrReview({ ...answers, [currentIndex]: labels || undefined });
  };

  const handleSkip = () => advanceOrReview({ ...answers, [currentIndex]: undefined });

  const handleOtherSubmit = () => {
    const trimmed = otherValue.trim();
    if (!trimmed) return;
    advanceOrReview({ ...answers, [currentIndex]: trimmed });
  };

  const handleBack = () => {
    if (showReview) {
      setShowReview(false);
    } else if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
      setOtherValue('');
      setFocusedIndex(null);
      focusedIndexRef.current = null;
    }
  };

  // ── Review screen ──────────────────────────────────────────────────────────
  if (showReview) {
    return (
      <div className="w-full px-4 pb-3 pt-2">
        <div
          className="mx-auto w-full max-w-3xl rounded-2xl"
          style={{ boxShadow: PANEL_SHADOW }}
        >
          <div className="overflow-hidden rounded-2xl border-2 border-action-primary bg-white dark:bg-gray-900">
            <div
              className="flex items-center justify-between border-b px-4 py-3"
              style={BANNER_STYLE}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className="h-2 w-2 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: BRAND_COLOR }}
                />
                <span className="truncate text-sm font-semibold" style={{ color: BRAND_COLOR }}>
                  {t('reviewBeforeIProceed')}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">
                  {t('answered', {
                    length: questions.filter((_, i) => answers[i]).length,
                    totalQuestions,
                  })}
                </span>
                <button
                  onClick={() => sendAllAnswers(answers)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title={t('close')}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="px-4 py-4">
              <div className="mb-4 space-y-3">
                {questions.map((q, i) => (
                  <div
                    key={i}
                    className="flex items-start justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-800/50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-400">{q.header}</p>
                      <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">
                        {q.question}
                      </p>
                      <p
                        className={`mt-1 text-sm font-medium ${
                          answers[i] ? 'text-action-primary' : 'italic text-gray-400'
                        }`}
                      >
                        {answers[i] ?? t('skipped')}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setCurrentIndex(i);
                        setShowReview(false);
                      }}
                      className="mt-0.5 flex-shrink-0 text-xs text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
                    >
                      {t('common:edit')}
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1 text-xs text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" />
                  {t('common:back')}
                </button>
                <button
                  onClick={() => sendAllAnswers(answers)}
                  className="bg-action-primary text-action-primary-text rounded-xl px-5 py-2 text-sm font-semibold"
                >
                  {t('submitAnswers')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Question screen ────────────────────────────────────────────────────────
  return (
    <div className="w-full px-4 pb-3 pt-2">
      <div
        className="mx-auto w-full max-w-3xl rounded-2xl"
        style={{ boxShadow: PANEL_SHADOW }}
      >
        <div className="overflow-hidden rounded-2xl border-2 border-action-primary bg-white dark:bg-gray-900">

          {/* Banner — always fully visible */}
          <div
            className="flex items-center justify-between border-b px-4 py-3"
            style={BANNER_STYLE}
          >
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              <span
                className="h-2 w-2 flex-shrink-0 animate-pulse rounded-full"
                style={{ backgroundColor: BRAND_COLOR }}
              />
              <span className="truncate text-sm font-semibold" style={{ color: BRAND_COLOR }}>
                {isPreparing
                  ? t('letMeThinkThroughSomething')
                  : totalQuestions === 1
                    ? t('iHaveAQuestionForYou')
                    : t('iHaveAFewQuestionsForYouHeading')}
              </span>
            </div>

            <div className="flex flex-shrink-0 items-center gap-2">
              {totalQuestions > 1 && (
                <div className="flex items-center gap-1.5">
                  <div className="flex gap-0.5">
                    {questions.map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 w-3 rounded-full transition-all duration-300 ${
                          i <= currentIndex
                            ? 'bg-action-primary'
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs tabular-nums text-gray-400">
                    {currentIndex + 1} / {totalQuestions}
                  </span>
                </div>
              )}
              {!isPreparing && (
                <button
                  onClick={() => sendAllAnswers(answers)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title={t('dismiss')}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Content — light frost when preparing */}
          <div className="relative px-4 py-4">
            {isPreparing && (
              <div
                className="absolute inset-0 z-10 backdrop-blur-[2px]"
                style={{ backgroundColor: 'rgba(255,255,255,0.35)' }}
              />
            )}

            {/* Header chip + question */}
            <div className="mb-3 space-y-1.5">
              {currentQuestion.header && (
                <span className="inline-block rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  {currentQuestion.header}
                </span>
              )}
              {currentQuestion.question && (
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {currentQuestion.question}
                </p>
              )}
            </div>

            {/* Options + optional preview pane */}
            <div className={hasPreview ? 'flex gap-3' : ''}>
              {/* Options list — mousemove on container, data-option-idx on each button */}
              <div
                className={`space-y-1.5 ${hasPreview ? 'w-1/2 flex-shrink-0' : ''}`}
                onMouseMove={handleOptionsMouseMove}
                onMouseLeave={scheduleClearFocus}
              >
                {options.map((option, optIndex) => {
                  const isSelected = selectedIndices.has(optIndex);
                  return (
                    <button
                      key={optIndex}
                      data-option-idx={optIndex}
                      onClick={() => handleOptionClick(optIndex)}
                      className={`group flex w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left text-sm transition-all duration-100
                        ${
                          isSelected
                            ? 'border-action-primary bg-action-background/50 shadow-sm'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white hover:shadow-sm dark:border-gray-700 dark:bg-gray-800/60 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                        }`}
                    >
                      {isMultiSelect && (
                        <div
                          className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border-2 transition-colors ${
                            isSelected
                              ? 'border-action-primary bg-action-primary'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          {isSelected && (
                            <svg
                              className="h-2.5 w-2.5 text-white"
                              fill="none"
                              viewBox="0 0 10 10"
                              stroke="currentColor"
                              strokeWidth={2.5}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M1.5 5L4 7.5L8.5 2" />
                            </svg>
                          )}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {option.label}
                        </div>
                        {option.description && (
                          <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {option.description}
                          </div>
                        )}
                      </div>
                      {!isMultiSelect && (
                        <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 transition-colors group-hover:text-gray-500 dark:text-gray-600" />
                      )}
                    </button>
                  );
                })}

                {/* Other — always-visible free-text input */}
                <div
                  className={`flex w-full items-center gap-2.5 rounded-xl border-2 px-3.5 py-3 transition-all duration-150 ${
                    otherFocused
                      ? 'border-action-primary bg-white shadow-[0_0_0_3px_rgba(255,115,46,0.15)] dark:bg-gray-900'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white dark:border-gray-700 dark:bg-gray-800/60'
                  }`}
                >
                  <PenLine
                    className={`h-4 w-4 flex-shrink-0 transition-colors ${
                      otherFocused ? 'text-action-primary' : 'text-gray-400'
                    }`}
                  />
                  <input
                    type="text"
                    value={otherValue}
                    onChange={(e) => setOtherValue(e.target.value)}
                    onFocus={() => setOtherFocused(true)}
                    onBlur={() => setOtherFocused(false)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleOtherSubmit(); }}
                    placeholder={t('otherTypeYourOwnAnswer')}
                    className="flex-1 border-0 bg-transparent text-sm text-gray-800 outline-none ring-0 focus:outline-none focus:ring-0 focus:shadow-none placeholder:text-gray-400 dark:text-gray-200"
                    style={{ boxShadow: 'none' }}
                  />
                  {otherValue.trim() && (
                    <button
                      onClick={handleOtherSubmit}
                      className="bg-action-primary text-action-primary-text flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold"
                    >
                      {isLastQuestion ? t('review') : t('next')}
                    </button>
                  )}
                </div>

                {/* Footer: back / skip / multiselect next */}
                <div className="flex items-center justify-between pt-0.5">
                  <div className="flex items-center gap-3">
                    {currentIndex > 0 && (
                      <button
                        onClick={handleBack}
                        className="flex items-center gap-1 text-xs text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
                      >
                        <ChevronLeftIcon className="h-3 w-3" />
                        {t('common:back')}
                      </button>
                    )}
                    <button
                      onClick={handleSkip}
                      className="text-xs text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
                    >
                      {t('skip')}
                    </button>
                  </div>
                  {isMultiSelect && selectedIndices.size > 0 && (
                    <button
                      onClick={handleMultiSelectNext}
                      className="bg-action-primary text-action-primary-text rounded-lg px-3 py-1.5 text-xs font-semibold"
                    >
                      {isLastQuestion ? t('review') : t('next')}
                    </button>
                  )}
                </div>
              </div>

              {/* Preview pane — fixed height prevents layout shift during hover */}
              {hasPreview && (
                <div
                  className="h-48 flex-1 overflow-auto rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800"
                  onMouseEnter={cancelClearFocus}
                  onMouseLeave={scheduleClearFocus}
                >
                  {focusedOption?.preview ? (
                    <div className="font-mono text-xs">
                      <ExtendedMarkdown content={focusedOption.preview} />
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">
                      {t('hoverAnOptionToPreview')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
